import { useEffect, useRef } from 'react';
import { MODAL_TYPES, type ModalType } from '@components/modals';
import { useEventTracking } from '@services/statsig';
import { useAppSelector, useModal } from '@store/hooks';
import { selectCurrentModal } from '@store/selectors/modalSelectors';

import type { SystemNotification } from '../types';

import { useActiveNotifications } from './useActiveNotifications';

const SYSTEM_NOTIFICATION_MODAL = MODAL_TYPES.SYSTEM_NOTIFICATION as ModalType;

interface SystemNotificationModalProps {
  notification?: SystemNotification;
}

/**
 * Hook that automatically opens system notification modals when they become active,
 * and closes them when they are no longer active (e.g., disabled in config).
 */
export function useSystemNotificationModals() {
  const { open, close } = useModal();
  const { modals } = useActiveNotifications();
  const currentModal = useAppSelector(selectCurrentModal);
  const { systemNotification } = useEventTracking();
  const shownModalsRef = useRef<Set<string>>(new Set());

  // Auto-close modal if its notification is no longer active
  useEffect(() => {
    if (currentModal?.modalType === SYSTEM_NOTIFICATION_MODAL) {
      const props = currentModal.modalProps as SystemNotificationModalProps;
      const notificationId = props.notification?.id;

      if (notificationId) {
        const isStillActive = modals.some(m => m.id === notificationId);

        if (!isStillActive) {
          // Notification was removed from config - close the modal
          close();
          shownModalsRef.current.delete(notificationId);
        }
      }
    }
  }, [currentModal, modals, close]);

  // Open new modals
  useEffect(() => {
    // Find the next modal that hasn't been shown yet
    const nextModal = modals.find(m => !shownModalsRef.current.has(m.id));

    if (nextModal) {
      // Mark as shown
      shownModalsRef.current.add(nextModal.id);

      // Track modal opened
      systemNotification.modalOpened({
        notification_id: nextModal.id,
        notification_type: 'modal',
        severity: nextModal.severity,
      });

      // Open the modal
      open(SYSTEM_NOTIFICATION_MODAL, { notification: nextModal });
    }
  }, [modals, open, systemNotification]);
}
